import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import IconFacebook from '@spothero/icons/facebook';
import Button from '@spothero/ui/button';
import Checkbox from '@spothero/ui/checkbox';
import Form from '@spothero/ui/form';
import FormElementError from '@spothero/ui/form-element-error';
import FormGroup from '@spothero/ui/form-group';
import FormGroupError from '@spothero/ui/form-group-error';
import HorizontalRule from '@spothero/ui/horizontal-rule';
import Image from 'common/image/Image';
import Label from '@spothero/ui/label';
import PasswordControl from '@spothero/ui/password-control';
import TextButton from '@spothero/ui/text-button';
import DOMUtils from '@spothero/utils/dom';
import userAuthenticationPropTypes from './userAuthenticationPropTypes';
import WithUserAuthentication from './WithUserAuthentication';
import FacebookEmailModal from './facebook/FacebookEmailModal';
import ForgotPasswordModal from './password/ForgotPasswordModal';
import EmailControl from 'common/email/EmailControl';

export class UserAuthentication extends Component {
    static propTypes = {
        ...userAuthenticationPropTypes,
        referralSource: PropTypes.string.isRequired,
        focus: PropTypes.bool,
        onLoginToggle: PropTypes.func.isRequired,
    };
    state = {
        showForgotPasswordModal: false,
    };

    _onTabClick = evt => {
        const {register, onToggleRegisterClick} = this.props;
        const id = DOMUtils.attr(evt.currentTarget, 'data-id');

        if ((id === 'login' && !register) || (id === 'register' && register)) {
            return;
        }

        onToggleRegisterClick();
    };

    _onForgotPasswordClick = evt => {
        this.setState({
            showForgotPasswordModal: true,
        });
    };

    _onForgotPasswordModalHidden = evt => {
        this.setState({
            showForgotPasswordModal: false,
        });
    };

    submitPromoCode() {
        return this._promocode.submit();
    }

    submitPassword() {
        return this._password.submit();
    }

    render() {
        const {
            register,
            formLoading,
            formGroupError,
            socialAuthError,
            emailError,
            showFacebookEmailModal,
            socialAuthLoading,
            onFacebookClick,
            onGoogleClick,
            onSubmit,
            onUserFieldChange,
            onFacebookEmailModalHidden,
            referralSource,
            focus,
            recaptchaError,
            onLoginToggle,
        } = this.props;
        const {showForgotPasswordModal} = this.state;
        const emailFormGroupClasses = classNames(
            'FormGroup-responsive',
            'email-form-group',
            {ContainsErrors: formGroupError},
            {'ContainsErrors-email': emailError}
        );
        const loginTabClasses = classNames('Tab', {'Tab-active': !register});
        const registerTabClasses = classNames('Tab', {'Tab-active': register});

        return (
            <Form
                id="UserAuthentication-form"
                className="UserAuthentication"
                loading={formLoading}
                onSubmit={onSubmit}
            >
                <div className="UserAuthentication-tabs">
                    <Button
                        className={loginTabClasses}
                        role="tab"
                        data-id="login"
                        onClick={this._onTabClick}
                    >
                        Sign In
                    </Button>
                    <Button
                        className={registerTabClasses}
                        role="tab"
                        data-id="register"
                        onClick={this._onTabClick}
                    >
                        Create Account
                    </Button>
                </div>
                <FormGroup className={emailFormGroupClasses}>
                    <EmailControl
                        required
                        className="UserAuthentication-email"
                        onChange={onUserFieldChange}
                        focus={focus}
                    />
                </FormGroup>
                <FormGroup className={emailFormGroupClasses}>
                    <PasswordControl
                        ref={node => {
                            this._password = node;
                        }}
                        label={<Label>Password</Label>}
                        showForgotPassword={!register}
                        onForgotPasswordClick={this._onForgotPasswordClick}
                        onFocus={onUserFieldChange}
                    />
                </FormGroup>
                {formGroupError && (
                    <FormGroupError>{formGroupError}</FormGroupError>
                )}
                {!register && (
                    <FormGroup className="UserAuthentication-toggle-remember">
                        <Checkbox
                            id="UserAuthentication-stay-signed-in"
                            name="remember"
                            label={<Label>Stay signed in</Label>}
                            defaultChecked
                        />
                    </FormGroup>
                )}
                {register && <div className="g-recaptcha" />}
                {register && recaptchaError && (
                    <FormElementError>This field is required.</FormElementError>
                )}
                <div className="FormActions">
                    <Button
                        className="UserAuthentication-submit"
                        color="primary"
                        type="submit"
                        loading={formLoading}
                    >
                        {register ? 'Create Account' : 'Sign In'}
                    </Button>
                    <TextButton onClick={onLoginToggle}>
                        Return to Guest Checkout
                    </TextButton>
                </div>
                {register && (
                    <p className="help-block">
                        By creating an account, you are agreeing to the{' '}
                        <a
                            href="/terms-of-use"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            SpotHero Terms and Conditions
                        </a>{' '}
                        and{' '}
                        <a
                            target="_blank"
                            href="/privacy-policy"
                            rel="noopener noreferrer"
                        >
                            Privacy Policy
                        </a>
                        .
                    </p>
                )}
                <HorizontalRule
                    className="UserAuthentication-separator"
                    lineStyle="solid"
                >
                    or
                </HorizontalRule>
                <FormGroup className="UserAuthentication-social-buttons">
                    <Button
                        className="Button-social"
                        color="secondary"
                        onClick={onFacebookClick}
                        iconPosition="left"
                        loading={socialAuthLoading}
                        disabled={socialAuthLoading}
                    >
                        <IconFacebook /> Facebook
                    </Button>
                    <Button
                        className="Button-social"
                        color="secondary"
                        onClick={onGoogleClick}
                        loading={socialAuthLoading}
                        disabled={socialAuthLoading}
                    >
                        <Image
                            className="Button-google-image"
                            width={16}
                            height={16}
                            cloudinaryImageId="s4b-admin-portal/google-g-logo"
                        />
                        Google
                    </Button>
                </FormGroup>
                {socialAuthError && (
                    <FormGroupError>{socialAuthError}</FormGroupError>
                )}
                {showFacebookEmailModal && (
                    <FacebookEmailModal
                        referralSource={referralSource}
                        onHidden={onFacebookEmailModalHidden}
                    />
                )}
                {showForgotPasswordModal && (
                    <ForgotPasswordModal
                        onHidden={this._onForgotPasswordModalHidden}
                    />
                )}
            </Form>
        );
    }
}

export default WithUserAuthentication(UserAuthentication);
